/**
 * Tasks Domain Seed Data
 *
 * Contains all seed data for Case Tasks:
 * - Tasks (action items on cases)
 * - Task Templates
 * - Task Assignments
 *
 * Tasks are action items associated with cases that need to be completed
 * by team members as part of case processing workflows.
 *
 * @see KB Article: kb-business-case-detail
 */

import { seedId } from '../utils';
import { CASE_IDS } from './cases';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// TASK IDs
// =============================================================================

export const TASK_IDS = {
  // Marcus Thompson's Portfolio Expansion Case Tasks
  MARCUS_VERIFY_INCOME: seedId('task-marcus-income', 1),
  MARCUS_PULL_CREDIT: seedId('task-marcus-credit', 1),
  MARCUS_ORDER_APPRAISAL: seedId('task-marcus-appraisal', 1),
  MARCUS_REVIEW_TITLE: seedId('task-marcus-title', 1),
  MARCUS_PREPARE_CLOSING: seedId('task-marcus-closing', 1),

  // David Chen's Payment Issue Case Tasks
  DAVID_INVESTIGATE_PAYMENT: seedId('task-david-investigate', 1),
  DAVID_CONTACT_BANK: seedId('task-david-bank', 1),
  DAVID_UPDATE_ACH: seedId('task-david-ach', 1),

  // Jessica Williams's Inspection Case Tasks
  JESSICA_SCHEDULE_INSPECTION: seedId('task-jessica-schedule', 1),
  JESSICA_REVIEW_PHOTOS: seedId('task-jessica-photos', 1),
  JESSICA_CALCULATE_DRAW: seedId('task-jessica-draw', 1),

  // Robert Garcia's Payoff Case Tasks
  ROBERT_REQUEST_PAYOFF: seedId('task-robert-payoff', 1),
  ROBERT_VERIFY_AMOUNT: seedId('task-robert-verify', 1),
  ROBERT_PROCESS_PAYOFF: seedId('task-robert-process', 1),

  // Maria Rodriguez's Construction Case Tasks (1Sharpe)
  MARIA_REVIEW_PLANS: seedId('task-maria-plans', 1),
  MARIA_APPROVE_CONTRACTOR: seedId('task-maria-contractor', 1),
  MARIA_SETUP_DRAW_SCHEDULE: seedId('task-maria-drawschedule', 1),
} as const;

export type TaskId = (typeof TASK_IDS)[keyof typeof TASK_IDS];

// =============================================================================
// TASK TEMPLATE IDs
// =============================================================================

export const TASK_TEMPLATE_IDS = {
  // Loan Origination Templates
  VERIFY_INCOME: seedId('task-tpl-verify-income', 1),
  PULL_CREDIT: seedId('task-tpl-pull-credit', 1),
  ORDER_APPRAISAL: seedId('task-tpl-order-appraisal', 1),
  REVIEW_TITLE: seedId('task-tpl-review-title', 1),
  PREPARE_CLOSING_DOCS: seedId('task-tpl-closing-docs', 1),
  WIRE_FUNDS: seedId('task-tpl-wire-funds', 1),

  // Support Templates
  INVESTIGATE_ISSUE: seedId('task-tpl-investigate', 1),
  CONTACT_CUSTOMER: seedId('task-tpl-contact', 1),
  ESCALATE_TO_MANAGER: seedId('task-tpl-escalate', 1),

  // Construction Draw Templates
  SCHEDULE_INSPECTION: seedId('task-tpl-schedule-inspection', 1),
  REVIEW_INSPECTION_PHOTOS: seedId('task-tpl-review-photos', 1),
  CALCULATE_DRAW_AMOUNT: seedId('task-tpl-calc-draw', 1),
  APPROVE_DRAW_REQUEST: seedId('task-tpl-approve-draw', 1),
} as const;

export type TaskTemplateId = (typeof TASK_TEMPLATE_IDS)[keyof typeof TASK_TEMPLATE_IDS];

// =============================================================================
// TASK STATUS & PRIORITY
// =============================================================================

export const TASK_STATUSES = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  BLOCKED: 'BLOCKED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;

export type TaskStatus = (typeof TASK_STATUSES)[keyof typeof TASK_STATUSES];

export const TASK_PRIORITIES = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  URGENT: 'URGENT',
} as const;

export type TaskPriority = (typeof TASK_PRIORITIES)[keyof typeof TASK_PRIORITIES];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface TaskSeedData {
  id: string;
  businessId: string;
  caseId: string;
  title: string;
  description?: string;
  templateId?: string;
  assigneeId?: string;
  assignedBy?: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate?: string;
  completedAt?: string;
  completedBy?: string;
  estimatedMinutes?: number;
  actualMinutes?: number;
  blockedReason?: string;
  dependencies?: string[]; // Task IDs that must complete first
  metadata?: Record<string, unknown>;
}

export interface TaskTemplateSeedData {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  category: string;
  defaultPriority: TaskPriority;
  estimatedMinutes?: number;
  instructions?: string;
  requiredFields?: string[];
  checklistItems?: string[];
  autoAssignRole?: string;
  slaHours?: number;
  isActive: boolean;
}

// =============================================================================
// TASK TEMPLATE DATA
// =============================================================================

export const TASK_TEMPLATES: Record<keyof typeof TASK_TEMPLATE_IDS, TaskTemplateSeedData> = {
  // ==========================================================================
  // LOAN ORIGINATION TEMPLATES
  // ==========================================================================
  VERIFY_INCOME: {
    id: TASK_TEMPLATE_IDS.VERIFY_INCOME,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Verify Income Documentation',
    description: 'Review and verify borrower income documentation',
    category: 'underwriting',
    defaultPriority: TASK_PRIORITIES.HIGH,
    estimatedMinutes: 30,
    instructions:
      'Review pay stubs, tax returns, and bank statements. Verify employment and calculate qualifying income.',
    requiredFields: ['income_docs', 'employment_verification'],
    checklistItems: [
      'Review last 2 years tax returns',
      'Verify employment status',
      'Calculate monthly income',
      'Document any discrepancies',
    ],
    autoAssignRole: 'underwriter',
    slaHours: 4,
    isActive: true,
  },
  PULL_CREDIT: {
    id: TASK_TEMPLATE_IDS.PULL_CREDIT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Pull Credit Report',
    description: 'Order and review tri-merge credit report',
    category: 'underwriting',
    defaultPriority: TASK_PRIORITIES.HIGH,
    estimatedMinutes: 15,
    instructions: 'Order tri-merge credit report. Review credit score, trade lines, and public records.',
    checklistItems: [
      'Order tri-merge report',
      'Review credit score',
      'Analyze trade lines',
      'Check for derogatories',
      'Document findings',
    ],
    autoAssignRole: 'processor',
    slaHours: 2,
    isActive: true,
  },
  ORDER_APPRAISAL: {
    id: TASK_TEMPLATE_IDS.ORDER_APPRAISAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Order Property Appraisal',
    description: 'Order appraisal through approved vendor',
    category: 'valuation',
    defaultPriority: TASK_PRIORITIES.MEDIUM,
    estimatedMinutes: 20,
    instructions: 'Select appropriate appraisal product type. Order through AMC portal. Track for completion.',
    checklistItems: [
      'Determine appraisal type needed',
      'Submit order to AMC',
      'Track order status',
      'Review completed appraisal',
    ],
    autoAssignRole: 'processor',
    slaHours: 8,
    isActive: true,
  },
  REVIEW_TITLE: {
    id: TASK_TEMPLATE_IDS.REVIEW_TITLE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Review Title Commitment',
    description: 'Review title commitment and clear exceptions',
    category: 'closing',
    defaultPriority: TASK_PRIORITIES.HIGH,
    estimatedMinutes: 45,
    instructions: 'Review title commitment for exceptions. Work with title company to clear any issues.',
    checklistItems: [
      'Review Schedule A',
      'Analyze Schedule B exceptions',
      'Verify legal description',
      'Clear title exceptions',
      'Confirm title insurance coverage',
    ],
    autoAssignRole: 'closer',
    slaHours: 4,
    isActive: true,
  },
  PREPARE_CLOSING_DOCS: {
    id: TASK_TEMPLATE_IDS.PREPARE_CLOSING_DOCS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Prepare Closing Documents',
    description: 'Generate and review closing package',
    category: 'closing',
    defaultPriority: TASK_PRIORITIES.HIGH,
    estimatedMinutes: 60,
    instructions: 'Generate closing documents. Review for accuracy. Prepare closing instructions.',
    checklistItems: [
      'Generate note and mortgage/deed of trust',
      'Prepare closing disclosure',
      'Review all amounts',
      'Prepare wire instructions',
      'Send to title/escrow',
    ],
    autoAssignRole: 'closer',
    slaHours: 8,
    isActive: true,
  },
  WIRE_FUNDS: {
    id: TASK_TEMPLATE_IDS.WIRE_FUNDS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Wire Loan Funds',
    description: 'Initiate wire transfer for loan funding',
    category: 'funding',
    defaultPriority: TASK_PRIORITIES.URGENT,
    estimatedMinutes: 30,
    instructions: 'Verify all conditions satisfied. Initiate wire per closing instructions.',
    checklistItems: [
      'Verify all closing conditions met',
      'Confirm wire instructions',
      'Initiate wire transfer',
      'Confirm wire received',
      'Update loan status',
    ],
    autoAssignRole: 'funder',
    slaHours: 2,
    isActive: true,
  },

  // ==========================================================================
  // SUPPORT TEMPLATES
  // ==========================================================================
  INVESTIGATE_ISSUE: {
    id: TASK_TEMPLATE_IDS.INVESTIGATE_ISSUE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Investigate Support Issue',
    description: 'Research and investigate customer support issue',
    category: 'support',
    defaultPriority: TASK_PRIORITIES.MEDIUM,
    estimatedMinutes: 30,
    instructions: 'Review customer history. Research the issue. Document findings.',
    checklistItems: [
      'Review account history',
      'Research issue details',
      'Document root cause',
      'Identify resolution options',
    ],
    autoAssignRole: 'support',
    slaHours: 4,
    isActive: true,
  },
  CONTACT_CUSTOMER: {
    id: TASK_TEMPLATE_IDS.CONTACT_CUSTOMER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Contact Customer',
    description: 'Reach out to customer regarding their inquiry',
    category: 'support',
    defaultPriority: TASK_PRIORITIES.MEDIUM,
    estimatedMinutes: 15,
    instructions: 'Contact customer via preferred method. Document conversation.',
    checklistItems: [
      'Prepare talking points',
      'Contact customer',
      'Document conversation',
      'Schedule follow-up if needed',
    ],
    autoAssignRole: 'support',
    slaHours: 2,
    isActive: true,
  },
  ESCALATE_TO_MANAGER: {
    id: TASK_TEMPLATE_IDS.ESCALATE_TO_MANAGER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Escalate to Manager',
    description: 'Escalate complex issue to management',
    category: 'support',
    defaultPriority: TASK_PRIORITIES.HIGH,
    estimatedMinutes: 20,
    instructions: 'Prepare escalation summary. Notify manager. Transfer case.',
    checklistItems: ['Document issue summary', 'List resolution attempts', 'Notify manager', 'Transfer ownership'],
    autoAssignRole: 'manager',
    slaHours: 1,
    isActive: true,
  },

  // ==========================================================================
  // CONSTRUCTION DRAW TEMPLATES
  // ==========================================================================
  SCHEDULE_INSPECTION: {
    id: TASK_TEMPLATE_IDS.SCHEDULE_INSPECTION,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Schedule Site Inspection',
    description: 'Schedule construction site inspection with inspector',
    category: 'construction',
    defaultPriority: TASK_PRIORITIES.MEDIUM,
    estimatedMinutes: 15,
    instructions: 'Contact inspector. Schedule site visit. Notify contractor.',
    checklistItems: [
      'Review draw request details',
      'Contact approved inspector',
      'Schedule site visit',
      'Notify contractor of inspection',
    ],
    autoAssignRole: 'draw_coordinator',
    slaHours: 8,
    isActive: true,
  },
  REVIEW_INSPECTION_PHOTOS: {
    id: TASK_TEMPLATE_IDS.REVIEW_INSPECTION_PHOTOS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Review Inspection Photos',
    description: 'Review photos from construction site inspection',
    category: 'construction',
    defaultPriority: TASK_PRIORITIES.HIGH,
    estimatedMinutes: 30,
    instructions: 'Review all inspection photos. Verify work completion matches draw request.',
    checklistItems: [
      'Review all photos',
      'Compare to draw request scope',
      'Verify completion percentage',
      'Document any discrepancies',
    ],
    autoAssignRole: 'draw_coordinator',
    slaHours: 4,
    isActive: true,
  },
  CALCULATE_DRAW_AMOUNT: {
    id: TASK_TEMPLATE_IDS.CALCULATE_DRAW_AMOUNT,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Calculate Draw Amount',
    description: 'Calculate approved draw amount based on completion',
    category: 'construction',
    defaultPriority: TASK_PRIORITIES.HIGH,
    estimatedMinutes: 20,
    instructions: 'Calculate draw based on completion. Apply holdback. Prepare approval.',
    checklistItems: [
      'Verify completion percentage',
      'Calculate line item amounts',
      'Apply retainage/holdback',
      'Prepare draw approval form',
    ],
    autoAssignRole: 'draw_coordinator',
    slaHours: 2,
    isActive: true,
  },
  APPROVE_DRAW_REQUEST: {
    id: TASK_TEMPLATE_IDS.APPROVE_DRAW_REQUEST,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Approve Draw Request',
    description: 'Final approval of construction draw request',
    category: 'construction',
    defaultPriority: TASK_PRIORITIES.URGENT,
    estimatedMinutes: 15,
    instructions: 'Review draw package. Verify all approvals. Authorize disbursement.',
    checklistItems: ['Review inspection report', 'Verify calculations', 'Check lien waivers', 'Authorize disbursement'],
    autoAssignRole: 'manager',
    slaHours: 4,
    isActive: true,
  },
};

// =============================================================================
// TASK DATA
// =============================================================================

export const TASKS: Record<keyof typeof TASK_IDS, TaskSeedData> = {
  // ==========================================================================
  // MARCUS THOMPSON - PORTFOLIO EXPANSION CASE
  // ==========================================================================
  MARCUS_VERIFY_INCOME: {
    id: TASK_IDS.MARCUS_VERIFY_INCOME,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.MARCUS_PORTFOLIO_EXPANSION,
    title: 'Verify Income Documentation',
    description: 'Review K-1s and business tax returns for portfolio income',
    templateId: TASK_TEMPLATE_IDS.VERIFY_INCOME,
    assigneeId: USER_IDS.SARAH_CHEN,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    status: TASK_STATUSES.COMPLETED,
    priority: TASK_PRIORITIES.HIGH,
    dueDate: '2026-01-21T17:00:00Z',
    completedAt: '2026-01-20T15:30:00Z',
    completedBy: USER_IDS.SARAH_CHEN,
    estimatedMinutes: 45,
    actualMinutes: 40,
  },
  MARCUS_PULL_CREDIT: {
    id: TASK_IDS.MARCUS_PULL_CREDIT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.MARCUS_PORTFOLIO_EXPANSION,
    title: 'Pull Credit Report',
    description: 'Order and review tri-merge credit report',
    templateId: TASK_TEMPLATE_IDS.PULL_CREDIT,
    assigneeId: USER_IDS.MIKE_JOHNSON,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    status: TASK_STATUSES.COMPLETED,
    priority: TASK_PRIORITIES.HIGH,
    dueDate: '2026-01-20T12:00:00Z',
    completedAt: '2026-01-20T10:15:00Z',
    completedBy: USER_IDS.MIKE_JOHNSON,
    estimatedMinutes: 15,
    actualMinutes: 12,
  },
  MARCUS_ORDER_APPRAISAL: {
    id: TASK_IDS.MARCUS_ORDER_APPRAISAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.MARCUS_PORTFOLIO_EXPANSION,
    title: 'Order Appraisals for All Properties',
    description: 'Order appraisals for the 3-property portfolio',
    templateId: TASK_TEMPLATE_IDS.ORDER_APPRAISAL,
    assigneeId: USER_IDS.MIKE_JOHNSON,
    assignedBy: USER_IDS.SARAH_CHEN,
    status: TASK_STATUSES.IN_PROGRESS,
    priority: TASK_PRIORITIES.MEDIUM,
    dueDate: '2026-01-22T17:00:00Z',
    estimatedMinutes: 60,
    metadata: { propertyCount: 3 },
  },
  MARCUS_REVIEW_TITLE: {
    id: TASK_IDS.MARCUS_REVIEW_TITLE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.MARCUS_PORTFOLIO_EXPANSION,
    title: 'Review Title Commitments',
    description: 'Review title commitments for all 3 properties',
    templateId: TASK_TEMPLATE_IDS.REVIEW_TITLE,
    status: TASK_STATUSES.PENDING,
    priority: TASK_PRIORITIES.HIGH,
    dueDate: '2026-01-25T17:00:00Z',
    estimatedMinutes: 90,
    dependencies: [TASK_IDS.MARCUS_ORDER_APPRAISAL],
  },
  MARCUS_PREPARE_CLOSING: {
    id: TASK_IDS.MARCUS_PREPARE_CLOSING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.MARCUS_PORTFOLIO_EXPANSION,
    title: 'Prepare Closing Documents',
    description: 'Generate closing package for portfolio financing',
    templateId: TASK_TEMPLATE_IDS.PREPARE_CLOSING_DOCS,
    status: TASK_STATUSES.PENDING,
    priority: TASK_PRIORITIES.HIGH,
    dueDate: '2026-01-28T17:00:00Z',
    estimatedMinutes: 120,
    dependencies: [TASK_IDS.MARCUS_REVIEW_TITLE],
  },

  // ==========================================================================
  // DAVID CHEN - PAYMENT ISSUE CASE
  // ==========================================================================
  DAVID_INVESTIGATE_PAYMENT: {
    id: TASK_IDS.DAVID_INVESTIGATE_PAYMENT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.DAVID_ACH_FAILED,
    title: 'Investigate Failed ACH Payment',
    description: 'Research why the ACH payment failed',
    templateId: TASK_TEMPLATE_IDS.INVESTIGATE_ISSUE,
    assigneeId: USER_IDS.EMILY_DAVIS,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    status: TASK_STATUSES.IN_PROGRESS,
    priority: TASK_PRIORITIES.HIGH,
    dueDate: '2026-01-21T12:00:00Z',
    estimatedMinutes: 30,
    metadata: { failureReason: 'NSF', attemptCount: 2 },
  },
  DAVID_CONTACT_BANK: {
    id: TASK_IDS.DAVID_CONTACT_BANK,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.DAVID_ACH_FAILED,
    title: 'Contact Bank for Payment Details',
    description: 'Call bank to get specific failure reason',
    templateId: TASK_TEMPLATE_IDS.CONTACT_CUSTOMER,
    assigneeId: USER_IDS.EMILY_DAVIS,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    status: TASK_STATUSES.PENDING,
    priority: TASK_PRIORITIES.MEDIUM,
    dueDate: '2026-01-21T14:00:00Z',
    estimatedMinutes: 15,
    dependencies: [TASK_IDS.DAVID_INVESTIGATE_PAYMENT],
  },
  DAVID_UPDATE_ACH: {
    id: TASK_IDS.DAVID_UPDATE_ACH,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.DAVID_ACH_FAILED,
    title: 'Update ACH Information',
    description: 'Update borrower ACH details if needed',
    status: TASK_STATUSES.PENDING,
    priority: TASK_PRIORITIES.MEDIUM,
    dueDate: '2026-01-21T17:00:00Z',
    estimatedMinutes: 15,
    dependencies: [TASK_IDS.DAVID_CONTACT_BANK],
  },

  // ==========================================================================
  // JESSICA WILLIAMS - INSPECTION CASE
  // ==========================================================================
  JESSICA_SCHEDULE_INSPECTION: {
    id: TASK_IDS.JESSICA_SCHEDULE_INSPECTION,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.JESSICA_INSPECTION,
    title: 'Schedule Site Inspection',
    description: 'Schedule inspection for Draw #3',
    templateId: TASK_TEMPLATE_IDS.SCHEDULE_INSPECTION,
    assigneeId: USER_IDS.JAMES_WILSON,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    status: TASK_STATUSES.COMPLETED,
    priority: TASK_PRIORITIES.MEDIUM,
    dueDate: '2026-01-19T17:00:00Z',
    completedAt: '2026-01-19T11:00:00Z',
    completedBy: USER_IDS.JAMES_WILSON,
    estimatedMinutes: 15,
    actualMinutes: 10,
  },
  JESSICA_REVIEW_PHOTOS: {
    id: TASK_IDS.JESSICA_REVIEW_PHOTOS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.JESSICA_INSPECTION,
    title: 'Review Inspection Photos',
    description: 'Review photos from site inspection',
    templateId: TASK_TEMPLATE_IDS.REVIEW_INSPECTION_PHOTOS,
    assigneeId: USER_IDS.JAMES_WILSON,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    status: TASK_STATUSES.IN_PROGRESS,
    priority: TASK_PRIORITIES.HIGH,
    dueDate: '2026-01-21T12:00:00Z',
    estimatedMinutes: 30,
  },
  JESSICA_CALCULATE_DRAW: {
    id: TASK_IDS.JESSICA_CALCULATE_DRAW,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.JESSICA_INSPECTION,
    title: 'Calculate Draw Amount',
    description: 'Calculate approved draw based on completion',
    templateId: TASK_TEMPLATE_IDS.CALCULATE_DRAW_AMOUNT,
    status: TASK_STATUSES.PENDING,
    priority: TASK_PRIORITIES.HIGH,
    dueDate: '2026-01-21T17:00:00Z',
    estimatedMinutes: 20,
    dependencies: [TASK_IDS.JESSICA_REVIEW_PHOTOS],
  },

  // ==========================================================================
  // ROBERT GARCIA - PAYOFF CASE
  // ==========================================================================
  ROBERT_REQUEST_PAYOFF: {
    id: TASK_IDS.ROBERT_REQUEST_PAYOFF,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.ROBERT_PAYOFF_REQUEST,
    title: 'Request Payoff Statement',
    description: 'Generate payoff statement for borrower',
    assigneeId: USER_IDS.EMILY_DAVIS,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    status: TASK_STATUSES.COMPLETED,
    priority: TASK_PRIORITIES.MEDIUM,
    dueDate: '2026-01-18T17:00:00Z',
    completedAt: '2026-01-18T14:00:00Z',
    completedBy: USER_IDS.EMILY_DAVIS,
    estimatedMinutes: 15,
    actualMinutes: 12,
  },
  ROBERT_VERIFY_AMOUNT: {
    id: TASK_IDS.ROBERT_VERIFY_AMOUNT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.ROBERT_PAYOFF_REQUEST,
    title: 'Verify Payoff Amount',
    description: 'Verify payoff calculations are correct',
    assigneeId: USER_IDS.SARAH_CHEN,
    assignedBy: USER_IDS.RACHEL_MARTINEZ,
    status: TASK_STATUSES.IN_PROGRESS,
    priority: TASK_PRIORITIES.HIGH,
    dueDate: '2026-01-21T12:00:00Z',
    estimatedMinutes: 20,
    dependencies: [TASK_IDS.ROBERT_REQUEST_PAYOFF],
  },
  ROBERT_PROCESS_PAYOFF: {
    id: TASK_IDS.ROBERT_PROCESS_PAYOFF,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    caseId: CASE_IDS.ROBERT_PAYOFF_REQUEST,
    title: 'Process Payoff',
    description: 'Process payoff when funds received',
    status: TASK_STATUSES.PENDING,
    priority: TASK_PRIORITIES.HIGH,
    dueDate: '2026-01-25T17:00:00Z',
    estimatedMinutes: 30,
    dependencies: [TASK_IDS.ROBERT_VERIFY_AMOUNT],
  },

  // ==========================================================================
  // MARIA RODRIGUEZ - CONSTRUCTION (1SHARPE)
  // ==========================================================================
  MARIA_REVIEW_PLANS: {
    id: TASK_IDS.MARIA_REVIEW_PLANS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    caseId: CASE_IDS.MARIA_CONSTRUCTION,
    title: 'Review Construction Plans',
    description: 'Review architectural and construction plans',
    assigneeId: USER_IDS.SHARPE_ADMIN,
    assignedBy: USER_IDS.SHARPE_ADMIN,
    status: TASK_STATUSES.IN_PROGRESS,
    priority: TASK_PRIORITIES.HIGH,
    dueDate: '2026-01-22T17:00:00Z',
    estimatedMinutes: 120,
  },
  MARIA_APPROVE_CONTRACTOR: {
    id: TASK_IDS.MARIA_APPROVE_CONTRACTOR,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    caseId: CASE_IDS.MARIA_CONSTRUCTION,
    title: 'Approve General Contractor',
    description: 'Review and approve GC for project',
    assigneeId: USER_IDS.SHARPE_ADMIN,
    assignedBy: USER_IDS.SHARPE_ADMIN,
    status: TASK_STATUSES.PENDING,
    priority: TASK_PRIORITIES.HIGH,
    dueDate: '2026-01-23T17:00:00Z',
    estimatedMinutes: 45,
    dependencies: [TASK_IDS.MARIA_REVIEW_PLANS],
  },
  MARIA_SETUP_DRAW_SCHEDULE: {
    id: TASK_IDS.MARIA_SETUP_DRAW_SCHEDULE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    caseId: CASE_IDS.MARIA_CONSTRUCTION,
    title: 'Setup Draw Schedule',
    description: 'Create draw schedule based on construction timeline',
    assigneeId: USER_IDS.ANALYST,
    assignedBy: USER_IDS.SHARPE_ADMIN,
    status: TASK_STATUSES.PENDING,
    priority: TASK_PRIORITIES.MEDIUM,
    dueDate: '2026-01-24T17:00:00Z',
    estimatedMinutes: 60,
    dependencies: [TASK_IDS.MARIA_APPROVE_CONTRACTOR],
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllTasks(workspace?: string): TaskSeedData[] {
  const tasks = Object.values(TASKS);
  if (!workspace) return tasks;
  return tasks.map((t) => ({ ...t, title: `${t.title} [${workspace}]` }));
}

export function getAllTaskTemplates(workspace?: string): TaskTemplateSeedData[] {
  const templates = Object.values(TASK_TEMPLATES);
  if (!workspace) return templates;
  return templates.map((t) => ({ ...t, name: `${t.name} [${workspace}]` }));
}

export function getTasksByCase(caseId: string): TaskSeedData[] {
  return Object.values(TASKS).filter((t) => t.caseId === caseId);
}

export function getTasksByBusiness(businessId: string): TaskSeedData[] {
  return Object.values(TASKS).filter((t) => t.businessId === businessId);
}

export function getTasksByAssignee(assigneeId: string): TaskSeedData[] {
  return Object.values(TASKS).filter((t) => t.assigneeId === assigneeId);
}

export function getTasksByStatus(status: TaskStatus): TaskSeedData[] {
  return Object.values(TASKS).filter((t) => t.status === status);
}

export function getPendingTasks(): TaskSeedData[] {
  return getTasksByStatus(TASK_STATUSES.PENDING);
}

export function getInProgressTasks(): TaskSeedData[] {
  return getTasksByStatus(TASK_STATUSES.IN_PROGRESS);
}

export function getTaskTemplatesByBusiness(businessId: string): TaskTemplateSeedData[] {
  return Object.values(TASK_TEMPLATES).filter((t) => t.businessId === businessId);
}

export function getTaskTemplatesByCategory(category: string): TaskTemplateSeedData[] {
  return Object.values(TASK_TEMPLATES).filter((t) => t.category === category);
}
